import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import type {
  UseFormClearErrors,
  UseFormGetFieldState,
  UseFormGetValues,
  UseFormSetError,
  UseFormTrigger,
} from 'react-hook-form';

import translations from '../../../../../../../lang';
import { isAbortError } from '../../../../../../../utils/fetchJson';

import { useValidateTrialEmailMutation } from '../api/hooks/useValidateTrialEmailMutation';
import { TrialSignupApiError } from '../api/request';
import type { TrialSignupFormValues } from '../schema/schema';

const { TRIAL_SIGNUP } = translations['en-US'];

type EmailCheckResult = {
  email: string;
  valid: boolean;
};

type UseTrialEmailValidationParams = {
  setError: UseFormSetError<TrialSignupFormValues>;
  clearErrors: UseFormClearErrors<TrialSignupFormValues>;
  getValues: UseFormGetValues<TrialSignupFormValues>;
  getFieldState: UseFormGetFieldState<TrialSignupFormValues>;
  trigger: UseFormTrigger<TrialSignupFormValues>;
  initialEmail?: string;
};

export const useTrialEmailValidation = ({
  setError,
  clearErrors,
  getValues,
  getFieldState,
  trigger,
  initialEmail,
}: UseTrialEmailValidationParams) => {
  const {
    mutateAsync,
    isLoading: isEmailChecking,
    reset,
  } = useValidateTrialEmailMutation();
  const [lastEmailCheck, setLastEmailCheck] = useState<EmailCheckResult | null>(
    null
  );
  const emailAbortRef = useRef<AbortController | null>(null);

  useEffect(() => () => emailAbortRef.current?.abort(), []);

  const runEmailAvailabilityCheck = async (
    rawEmail: string
  ): Promise<boolean> => {
    const email = rawEmail.trim();

    if (!email) return false;

    if (lastEmailCheck?.email === email) {
      if (!lastEmailCheck.valid) return false;

      return true;
    }

    emailAbortRef.current?.abort();
    const controller = new AbortController();
    emailAbortRef.current = controller;

    try {
      const result = await mutateAsync({ email, signal: controller.signal });

      if (controller.signal.aborted) return false;

      setLastEmailCheck({ email, valid: result.valid });

      if (!result.valid) {
        setError('email', {
          type: 'server',
          message: result.message,
        });
        return false;
      }

      clearErrors('email');
      return true;
    } catch (error) {
      if (isAbortError(error)) return false;

      const message =
        error instanceof TrialSignupApiError
          ? error.message
          : TRIAL_SIGNUP.ERRORS.EMAIL_VALIDATE_FAILED;

      setLastEmailCheck({ email, valid: false });
      setError('email', {
        type: 'server',
        message,
      });
      return false;
    } finally {
      if (emailAbortRef.current === controller) {
        emailAbortRef.current = null;
      }
    }
  };

  useEffect(() => {
    const email = initialEmail?.trim();
    if (!email) return undefined;

    let cancelled = false;

    const validateInitialEmail = async () => {
      const isFormatValid = await trigger('email');
      if (!isFormatValid || cancelled) return;

      await runEmailAvailabilityCheck(email);
    };

    validateInitialEmail();

    return () => {
      cancelled = true;
      emailAbortRef.current?.abort();
    };
  }, [initialEmail]);

  const handleEmailChange = (
    event: ChangeEvent<HTMLInputElement>,
    fieldOnChange: (event: ChangeEvent<HTMLInputElement>) => void
  ) => {
    const next = event.target.value;
    fieldOnChange(event);

    if (lastEmailCheck && next.trim() !== lastEmailCheck.email) {
      setLastEmailCheck(null);
      reset();
      const emailError = getFieldState('email').error;
      if (emailError?.type === 'server') {
        clearErrors('email');
      }
    }
  };

  const handleEmailBlur = async (fieldOnBlur: () => void) => {
    fieldOnBlur();

    const isFormatValid = await trigger('email');
    if (!isFormatValid) return;

    const email = getValues('email');
    if (!email?.trim()) return;

    await runEmailAvailabilityCheck(email);
  };

  const ensureEmailValidForSubmit = async (
    rawEmail: string
  ): Promise<boolean> => {
    const email = rawEmail.trim();
    const alreadyValid =
      lastEmailCheck?.email === email && lastEmailCheck.valid === true;

    if (alreadyValid) return true;

    return runEmailAvailabilityCheck(email);
  };

  const isEmailCheckBlockingSubmit =
    isEmailChecking || lastEmailCheck?.valid === false;

  const isEmailVerified = lastEmailCheck?.valid === true;

  return {
    isEmailChecking,
    isEmailCheckBlockingSubmit,
    isEmailVerified,
    handleEmailChange,
    handleEmailBlur,
    ensureEmailValidForSubmit,
  };
};
